import React, { useEffect, useState } from 'react';
import { Modal, Button, Spinner } from './UI';
import { submissionAPI, apiUrl } from '../utils/api';

// ─────────────────────────────────────────────────────────────────────────────
// RecordingModal — plays the Teams recording of one interview round.
//
// The video streams through our own API (a signed link, no Microsoft login
// needed), so anyone who can see the interview can watch it. Every state the
// lookup can end in is spelled out: nobody should wonder whether the feature
// is broken or the recording simply isn't there yet.
// ─────────────────────────────────────────────────────────────────────────────

const fmtDuration = (ms) => {
  if (!ms) return null;
  const s = Math.round(ms / 1000);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return h ? `${h}h ${m}m` : `${m} min`;
};
const fmtSize = (b) => (b ? `${(b / 1048576).toFixed(0)} MB` : null);
const fmtWhen = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || isNaN(d.getTime())) return '';
  return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true });
};

const MESSAGES = {
  'not-yet':        { icon: '⏳', title: 'Not published yet', text: 'Teams usually makes the recording available a few minutes after the meeting ends. Try again shortly.' },
  none:             { icon: '🎬', title: 'No recording found', text: 'Nothing was saved for this round. Either recording was off for the meeting, or nobody joined with a Teams account that could record.' },
  'not-ended':      { icon: '📅', title: 'Interview not finished', text: 'The recording can only appear after the scheduled slot is over.' },
  'no-meeting':     { icon: '💻', title: 'No Teams meeting', text: 'This round has no Teams meeting link, so there is nothing to record.' },
  'not-configured': { icon: '⚙️', title: 'Teams is not set up', text: 'A SuperAdmin needs to fill in Settings → Teams Meetings before recordings can be found.' },
  'no-permission':  { icon: '🔐', title: 'Recording access not granted', text: 'The Azure app needs one more permission: Microsoft Graph → Application → Files.Read.All, with admin consent. Settings → Teams Meetings shows the full list.' },
  'not-found':      { icon: '🔍', title: 'Round not found', text: 'This round was removed or changed since the page loaded. Refresh and open it again.' },
  'no-access':      { icon: '🚫', title: 'Not available to you', text: 'This interview is outside the clients and teams you are assigned to.' },
  'no-drive':       { icon: '📁', title: 'Recordings account has no OneDrive', text: 'Teams saves recordings to a co-organizer\'s OneDrive when the organizer has none. A SuperAdmin should set that account as the Recordings account in Settings → Teams Meetings.' },
  error:            { icon: '⚠️', title: 'Could not reach Teams', text: 'Something failed while looking for the recording. Try again in a moment.' },
};

export default function RecordingModal({ isOpen, onClose, submissionId, round }) {
  const [state, setState] = useState({ loading: true });
  const [current, setCurrent] = useState(0);

  const load = (refresh = false) => {
    setState({ loading: true });
    // round.id (when the round has one) pins the lookup to this exact round.
    submissionAPI.roundRecordings(submissionId, round.round, { refresh, roundId: round.id || undefined })
      .then(({ data }) => { setState({ loading: false, ...data }); setCurrent(0); })
      .catch((err) => {
        const code = err?.response?.status;
        const msg  = err?.response?.data?.message;
        // A 404 for the round itself (deleted/changed since this page loaded)
        // is not an access problem — say so.
        const status = code === 404 && /round/i.test(msg || '') ? 'not-found'
          : (code === 403 || code === 404 ? 'no-access' : 'error');
        setState({ loading: false, status, message: msg });
      });
  };

  useEffect(() => { if (isOpen) load(false); }, [isOpen, submissionId, round.round, round.id]); // eslint-disable-line

  const list = state.recordings || [];
  const rec = list[current];
  const title = `Round ${round.round}${round.title ? ` · ${round.title}` : ''} — recording`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="lg">
      {state.loading ? (
        <div className="flex flex-col items-center justify-center gap-3 py-14">
          <Spinner />
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Looking for the recording in Teams…</p>
        </div>
      ) : state.status === 'ready' && rec ? (
        <div className="space-y-3">
          {list.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {list.map((r, i) => (
                <button key={r.id} type="button" onClick={() => setCurrent(i)}
                  className={`rec-tab${i === current ? ' is-active' : ''}`}>
                  Part {i + 1}{r.durationMs ? ` · ${fmtDuration(r.durationMs)}` : ''}
                </button>
              ))}
            </div>
          )}

          {/* key forces a fresh <video> when switching parts */}
          <video key={rec.id} controls preload="metadata" playsInline className="rec-player"
            src={apiUrl(rec.viewUrl)}>
            Your browser cannot play this video. Use Download instead.
          </video>

          <div className="flex flex-wrap items-center justify-between gap-2 text-xs" style={{ color: 'var(--text-muted)' }}>
            <span className="truncate min-w-0" title={rec.name}>
              {rec.name}
              {rec.durationMs && <> · {fmtDuration(rec.durationMs)}</>}
              {rec.size ? <> · {fmtSize(rec.size)}</> : null}
              {rec.createdAt && <> · {fmtWhen(rec.createdAt)}</>}
            </span>
            <span className="rec-note" title={rec.matchedBy === 'subject' ? 'The file is named after this meeting' : "The file name carries the candidate's full name"}>
              {rec.matchedBy === 'subject' ? 'matched this meeting' : 'matched by candidate name'}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-1" style={{ borderTop: '1px solid var(--border-subtle)' }}>
            <a className="rec-link" href={apiUrl(rec.downloadUrl)}>⬇ Download</a>
            {rec.webUrl && (
              <a className="rec-link" href={rec.webUrl} target="_blank" rel="noopener noreferrer">Open in OneDrive ↗</a>
            )}
            <span className="flex-1" />
            <Button size="sm" variant="ghost" onClick={() => load(true)}>↻ Check again</Button>
            <Button size="sm" variant="secondary" onClick={onClose}>Close</Button>
          </div>
        </div>
      ) : (
        (() => {
          const m = MESSAGES[state.status] || MESSAGES.error;
          return (
            <div className="flex flex-col items-center text-center gap-2 py-10 px-4">
              <div style={{ fontSize: 40, opacity: 0.8 }}>{m.icon}</div>
              <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)', margin: 0 }}>{m.title}</p>
              <p className="text-xs" style={{ color: 'var(--text-muted)', margin: 0, maxWidth: 440, lineHeight: 1.6 }}>
                {state.message || m.text}
              </p>
              {state.status === 'not-yet' && state.endedAt && (
                <p className="text-xs" style={{ color: 'var(--text-muted)', margin: 0 }}>Meeting ended {fmtWhen(state.endedAt)}.</p>
              )}
              {state.account && ['not-yet', 'none', 'no-drive'].includes(state.status) && (
                <p className="text-xs" style={{ color: 'var(--text-muted)', margin: 0 }}>Looked in the OneDrive of {state.account}.</p>
              )}
              <div className="flex gap-2 pt-3">
                {['not-yet', 'none', 'error', 'no-permission'].includes(state.status) && (
                  <Button size="sm" variant="secondary" onClick={() => load(true)}>↻ Check again</Button>
                )}
                {round.meetingLink && (
                  <a className="rec-link" href={round.meetingLink} target="_blank" rel="noopener noreferrer">Open the Teams meeting ↗</a>
                )}
              </div>
            </div>
          );
        })()
      )}
    </Modal>
  );
}
